/**
 * UptimeTimeline Component
 *
 * Displays a 90-day visual timeline of system uptime inspired by ShipStation's status page.
 * Each day is represented by a colored bar:
 * - Green: 100% uptime (all checks green)
 * - Yellow: Some degradation (yellow checks, no red)
 * - Red: Incidents occurred (any red checks)
 * - Gray: No data available
 *
 * Features:
 * - Hover tooltips showing date, uptime %, and incident count
 * - Responsive design that compresses gracefully on mobile
 * - Click to show detailed day info
 */

import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import './UptimeTimeline.css';

/**
 * Determine bar color based on day's health data
 */
function getDayStatus(dayData) {
  if (!dayData || dayData.totalChecks === 0) {
    return 'no-data';
  }

  if (dayData.redChecks > 0) {
    return 'red';
  }

  if (dayData.yellowChecks > 0) {
    return 'yellow';
  }

  return 'green';
}

/**
 * Format date for display
 */
function formatDate(dateStr) {
  if (!dateStr) return 'Unknown';
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

/**
 * Format date for short display
 */
function formatDateShort(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric'
  });
}

/**
 * Single day bar component
 */
function DayBar({ dayData, date, index, totalDays }) {
  const status = getDayStatus(dayData);
  const isToday = index === 0;

  // Tooltip content
  const tooltipContent = dayData ? (
    <div className="uptime-tooltip-content">
      <div className="tooltip-date">{formatDate(date)}</div>
      {dayData.uptimePercent !== null && (
        <div className="tooltip-uptime">
          <strong>{dayData.uptimePercent}%</strong> uptime
        </div>
      )}
      {dayData.totalChecks > 0 && (
        <div className="tooltip-checks">
          {dayData.totalChecks} checks
          {dayData.redChecks > 0 && (
            <span className="text-danger ms-2">
              {dayData.redChecks} incidents
            </span>
          )}
          {dayData.yellowChecks > 0 && dayData.redChecks === 0 && (
            <span className="text-warning ms-2">
              {dayData.yellowChecks} warnings
            </span>
          )}
        </div>
      )}
    </div>
  ) : (
    <div className="uptime-tooltip-content">
      <div className="tooltip-date">{formatDate(date)}</div>
      <div className="tooltip-no-data">No data</div>
    </div>
  );

  return (
    <OverlayTrigger
      placement="top"
      delay={{ show: 100, hide: 100 }}
      overlay={
        <Tooltip className="uptime-tooltip">
          {tooltipContent}
        </Tooltip>
      }
    >
      <div
        className={`uptime-bar status-${status} ${isToday ? 'today' : ''}`}
        data-date={date}
        role="button"
        tabIndex={0}
        aria-label={`${formatDate(date)}: ${dayData?.uptimePercent ?? 'No data'}% uptime`}
      />
    </OverlayTrigger>
  );
}

/**
 * UptimeTimeline - Main component
 *
 * @param {Object} props
 * @param {Array} props.dailyData - Array of daily health objects sorted by date desc
 * @param {number} props.days - Number of days to display (default 90)
 * @param {boolean} props.loading - Loading state
 * @param {boolean} props.compact - Compact mode for smaller displays
 */
export function UptimeTimeline({
  dailyData = [],
  days = 90,
  loading = false,
  compact = false
}) {
  // Create array of last N days
  const today = new Date();
  const dayArray = [];

  for (let i = 0; i < days; i++) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    const dateStr = date.toISOString().split('T')[0];
    const dayData = dailyData.find(d => d.date === dateStr);
    dayArray.push({ date: dateStr, data: dayData });
  }

  // Reverse so oldest is on the left
  dayArray.reverse();

  if (loading) {
    return (
      <div className={`uptime-timeline ${compact ? 'compact' : ''}`}>
        <div className="uptime-timeline-loading">
          <div className="uptime-bars">
            {Array.from({ length: days }).map((_, i) => (
              <div key={i} className="uptime-bar status-loading" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`uptime-timeline ${compact ? 'compact' : ''}`}>
      <div className="uptime-timeline-labels">
        <span className="label-start">{days} days ago</span>
        <span className="label-end">Today</span>
      </div>
      <div className="uptime-bars">
        {dayArray.map((day, index) => (
          <DayBar
            key={day.date}
            date={day.date}
            dayData={day.data}
            index={dayArray.length - 1 - index}
            totalDays={days}
          />
        ))}
      </div>
      <div className="uptime-timeline-legend">
        <div className="legend-item">
          <span className="legend-dot status-green" />
          <span>Operational</span>
        </div>
        <div className="legend-item">
          <span className="legend-dot status-yellow" />
          <span>Degraded</span>
        </div>
        <div className="legend-item">
          <span className="legend-dot status-red" />
          <span>Incident</span>
        </div>
        <div className="legend-item">
          <span className="legend-dot status-no-data" />
          <span>No data</span>
        </div>
      </div>
    </div>
  );
}

/**
 * MiniUptimeTimeline - Compact version for service cards
 *
 * @param {Object} props
 * @param {Array} props.data - Array of { date, worstStatus, uptimePercent }
 * @param {number} props.days - Number of days to display (default 30)
 */
export function MiniUptimeTimeline({ data = [], days = 30 }) {
  // Create array of last N days
  const today = new Date();
  const dayArray = [];

  for (let i = 0; i < days; i++) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    const dateStr = date.toISOString().split('T')[0];
    const dayData = data.find(d => d.date === dateStr);
    dayArray.push({ date: dateStr, data: dayData });
  }

  // Reverse so oldest is on the left
  dayArray.reverse();

  return (
    <div className="mini-uptime-timeline">
      <div className="mini-uptime-bars">
        {dayArray.map((day) => {
          const status = day.data?.worstStatus || 'no-data';
          return (
            <OverlayTrigger
              key={day.date}
              placement="top"
              delay={{ show: 100, hide: 100 }}
              overlay={
                <Tooltip className="uptime-tooltip">
                  <div className="uptime-tooltip-content">
                    <div className="tooltip-date">{formatDateShort(day.date)}</div>
                    {day.data?.uptimePercent !== null && day.data?.uptimePercent !== undefined ? (
                      <div className="tooltip-uptime">
                        {day.data.uptimePercent}% uptime
                      </div>
                    ) : (
                      <div className="tooltip-no-data">No data</div>
                    )}
                  </div>
                </Tooltip>
              }
            >
              <div
                className={`mini-uptime-bar status-${status}`}
                data-date={day.date}
              />
            </OverlayTrigger>
          );
        })}
      </div>
    </div>
  );
}

export default UptimeTimeline;
